import type { TokenTools } from '@ardrive/turbo-sdk';
import { SOLToTokenAmount, TurboFactory } from '@ardrive/turbo-sdk/web';
import { isFeatureEnabled } from '../../config/featureFlags';
import { ARDRIVE_BACKEND_UPLOAD_MAX_BYTES } from '../../lib/config';
import { addBreadcrumb, captureAndReportError } from '../errorReporting';
import {
  createTurboSolanaWalletAdapter,
  getWalletPublicKeyBase58,
} from '../wallet';
import { BaseProvider } from './BaseProvider';
import { ArDriveBackendUploader } from './ardrive/backend/ArDriveBackendUploader';
import { createSolanaTokenTools } from './ardrive/solana/createSolanaTokenTools';
import { throwIfArDriveInsufficientCredits } from './ardrive/shared/errors';
import { buildArDriveTags } from './ardrive/shared/tags';
import type { ArDriveTag } from './ardrive/shared/types';
import { ArDriveTurboUploader } from './ardrive/turbo/ArDriveTurboUploader';
import type {
  ProviderConfig,
  ProviderTrpcClient,
  UploadOptions,
  UploadResult,
  WalletAdapter,
} from './types';

export class ArDriveProvider extends BaseProvider {
  name: 'ardrive' = 'ardrive';
  private trpcClient?: ProviderTrpcClient;
  private readonly turboUploader = new ArDriveTurboUploader({
    getErrorMessage: (error) => this.getErrorMessage(error),
    getTokenTools: (walletAdapter) =>
      this.trpcClient
        ? this.createSolanaTokenTools(this.trpcClient, walletAdapter)
        : undefined,
    reportProgress: (uploadedBytes, totalBytes, onProgress, additionalInfo) =>
      this.reportProgress(
        uploadedBytes,
        totalBytes,
        onProgress,
        additionalInfo,
      ),
    retryWithExponentialBackoff: (operation, options) =>
      this.retryWithExponentialBackoff(operation, options),
  });

  async initialize(config?: ProviderConfig): Promise<void> {
    if (!config?.walletAdapter) {
      throw new Error('ArDrive requires a connected Solana wallet');
    }

    this.trpcClient = config.trpcClient;

    try {
      await this.turboUploader.initialize(config.walletAdapter);
    } catch (error) {
      throw new Error(
        `Failed to initialize ArDrive with wallet adapter: ${this.getErrorMessage(error)}`,
      );
    }
  }

  private shouldUseBackendUpload(sizeInBytes: number): boolean {
    if (!isFeatureEnabled('useBackendArDriveUpload')) {
      return false;
    }
    return sizeInBytes <= ARDRIVE_BACKEND_UPLOAD_MAX_BYTES;
  }

  private async uploadViaBackend(
    buffer: Buffer,
    tags: ArDriveTag[],
    options?: UploadOptions,
  ): Promise<UploadResult> {
    const { signedBytesBuffer, dataItemSize } =
      await this.turboUploader.signDataItem(buffer, tags, options);
    const backendUploader = new ArDriveBackendUploader({
      trpcClient: this.trpcClient,
      reportProgress: (uploadedBytes, totalBytes, onProgress, additionalInfo) =>
        this.reportProgress(
          uploadedBytes,
          totalBytes,
          onProgress,
          additionalInfo,
        ),
      withRetry: (operation, operationName, customMaxRetries) =>
        this.withRetry(operation, operationName, customMaxRetries),
    });
    const uploadResponse = await backendUploader.uploadSignedDataItem(
      signedBytesBuffer,
      dataItemSize,
      options,
    );

    return {
      id: uploadResponse.id,
      url: uploadResponse.url,
      timestamp: Date.now(),
      size: dataItemSize,
    };
  }

  async uploadFromBuffer(
    buffer: Buffer,
    options?: UploadOptions,
  ): Promise<UploadResult> {
    if (!this.turboUploader.isInitialized()) {
      throw new Error('ArDrive provider not initialized');
    }

    const tags = buildArDriveTags(options);

    addBreadcrumb('ArDrive upload starting', 'arweave', {
      bufferSize: buffer.length,
      tagCount: tags.length,
      tags: tags.map((t) => `${t.name}:${t.value.length}chars`),
      contentType: options?.contentType,
      backendUpload: this.shouldUseBackendUpload(buffer.length),
    });

    try {
      if (this.shouldUseBackendUpload(buffer.length)) {
        try {
          return await this.uploadViaBackend(buffer, tags, options);
        } catch (error) {
          const status = (error as { status?: number }).status;
          if (status === 413) {
            return await this.turboUploader.uploadBuffer(buffer, tags, options);
          }
          throw error;
        }
      }

      return await this.turboUploader.uploadBuffer(buffer, tags, options);
    } catch (error) {
      throwIfArDriveInsufficientCredits(error);
      throw error;
    }
  }

  async getBalance(): Promise<{ balance: string; currency: string }> {
    return this.turboUploader.getBalance();
  }

  async getUploadCost(
    bytes: number,
  ): Promise<{ cost: string; currency: string }> {
    return this.turboUploader.getUploadCost(bytes);
  }

  async topUpBalance(
    amountInSol: number,
    trpcClient?: {
      recordArDriveTopup: {
        mutate: (args: {
          txId: string;
          amount: number;
          walletAddress: string;
          status:
            | 'completed'
            | 'failed'
            | 'pending_blockchain'
            | 'blockchain_confirmed'
            | 'pending_credits';
        }) => Promise<unknown>;
      };
    },
  ): Promise<{
    txId: string;
    status: 'pending' | 'confirmed' | 'failed';
    winc: string;
  }> {
    const wallet = this.turboUploader.getWalletAdapter();

    if (!wallet?.publicKey) {
      throw new Error(
        'Wallet adapter is required for top-up. Please connect your Solana wallet.',
      );
    }

    if (!this.turboUploader.isInitialized()) {
      throw new Error('ArDrive provider not initialized');
    }

    const topupClient = trpcClient ?? this.trpcClient;
    if (!topupClient) {
      throw new Error('tRPC client is required for ArDrive top-ups');
    }
    if (!this.trpcClient && trpcClient) {
      this.trpcClient = trpcClient as ProviderTrpcClient;
    }

    if (!this.trpcClient) {
      throw new Error('Full tRPC client is required for Solana token tools');
    }
    if (!wallet?.sendTransaction && !wallet?.signTransaction) {
      throw new Error(
        'Wallet adapter must support sendTransaction or signTransaction',
      );
    }

    const walletAdapterForTopup = createTurboSolanaWalletAdapter(wallet);
    const topUpTurbo = TurboFactory.authenticated({
      walletAdapter: walletAdapterForTopup,
      token: 'solana',
      tokenTools: this.createSolanaTokenTools(this.trpcClient, wallet),
    });
    const walletAddress = getWalletPublicKeyBase58(wallet);

    const topUpResponse = await topUpTurbo.topUpWithTokens({
      tokenAmount: SOLToTokenAmount(amountInSol),
    });

    if (topupClient) {
      const statusMap = {
        pending: 'pending_credits',
        confirmed: 'completed',
        failed: 'failed',
      } as const;

      try {
        await topupClient.recordArDriveTopup.mutate({
          txId: topUpResponse.id,
          amount: amountInSol,
          walletAddress,
          status: statusMap[topUpResponse.status],
        });
      } catch (error) {
        captureAndReportError('Failed to record ArDrive top-up', error);
      }
    }

    return {
      txId: topUpResponse.id,
      status: topUpResponse.status,
      winc: topUpResponse.winc,
    };
  }

  private createSolanaTokenTools(
    trpcClient: ProviderTrpcClient,
    walletAdapter: WalletAdapter,
  ): TokenTools {
    return createSolanaTokenTools({
      trpcClient,
      walletAdapter,
    });
  }

  async getFiatRates(): Promise<{ arToUsd: number }> {
    try {
      const unauthenticatedTurbo = TurboFactory.unauthenticated();
      const rates = await unauthenticatedTurbo.getFiatRates();

      console.log('Turbo fiat rates response:', JSON.stringify(rates, null, 2));

      let arToUsd = 0;

      // Response shape: { winc: "<winston>", fiat: { usd: <value> } }.
      // 1 AR = 1e12 winston, so arToUsd = (usd / winston) * 1e12.
      if (rates?.winc && rates.fiat && rates.fiat.usd) {
        const winstonAmount = parseFloat(rates.winc);
        const usdValue = rates.fiat.usd;
        arToUsd = (usdValue / winstonAmount) * 1e12;

        console.log('Calculation:', {
          winstonAmount,
          usdValue,
          arToUsd,
        });
      }

      console.log('Converted AR to USD rate:', arToUsd);

      if (arToUsd === 0 || Number.isNaN(arToUsd)) {
        console.log('Using fallback AR to USD rate: 10.50');
        arToUsd = 10.5;
      }

      return { arToUsd };
    } catch (error) {
      captureAndReportError('Failed to fetch fiat rates:', error);
      return { arToUsd: 10.5 };
    }
  }
}
